// src/components/admin/events/EventQRScannerModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import { recordAttendanceByQR } from '../../../api/eventsApi';
import { fmtDateTime } from '../../../api/eventsApi';

function EventQRScannerModal({ orgId, adminId, onClose }) {
  const qrCodeRef  = useRef(null);
  const startedRef = useRef(false);
  const fileInputRef = useRef(null);

  // 'camera' | 'upload'
  const [scanMode, setScanMode] = useState('camera');

  // 'scanning' | 'validating' | 'confirming' | 'recording' | 'success' | 'error'
  const [phase, setPhase] = useState('scanning');

  const [errorMsg,   setErrorMsg]   = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [uploadFileName,   setUploadFileName]   = useState('');

  // ── Camera: start ──────────────────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');

      let cameras = [];
      try { cameras = await Html5Qrcode.getCameras(); } catch (e) { throw e; }

      if (!cameras || cameras.length === 0)
        throw Object.assign(new Error('No cameras found'), { name: 'NotFoundError' });

      const backCamera = cameras.find(c => {
        const lbl = (c.label || '').toLowerCase();
        return lbl.includes('back') || lbl.includes('rear') || lbl.includes('environment');
      });

      const instance = new Html5Qrcode('bb-event-qr-reader');
      qrCodeRef.current = instance;

      await instance.start(
        (backCamera || cameras[0]).id,
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decodedText) => {
          await stopCamera();
          handleDecodedQR(decodedText);
        },
        () => {}
      );
    } catch (err) {
      const name = err?.name || '';
      const msg2 = err?.message || '';
      let msg = 'Could not start camera. ';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || msg2.toLowerCase().includes('permission'))
        msg += 'Camera permission was denied. Click the camera icon in your browser address bar, set it to "Allow", then try again.';
      else if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || msg2.toLowerCase().includes('no cameras'))
        msg += 'No camera was detected on this device.';
      else if (name === 'NotReadableError' || name === 'TrackStartError')
        msg += 'The camera is already in use by another app or tab. Close it and try again.';
      else
        msg += msg2 || 'Please allow camera access in your browser and try again.';
      setErrorMsg(msg);
      setPhase('error');
    }
  };

  // ── Camera: stop ───────────────────────────────────────────────────────────
  const stopCamera = async () => {
    const instance = qrCodeRef.current;
    if (!instance) return;
    try {
      const state = instance.getState();
      if (state === 2 || state === 3) await instance.stop();
      instance.clear();
    } catch (_) {}
    qrCodeRef.current = null;
  };

  // ── Mount / unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startCamera();
    return () => { stopCamera(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mode switching ─────────────────────────────────────────────────────────
  const handleModeSwitch = async (newMode) => {
    if (newMode === scanMode) return;
    setPhase('scanning');
    setErrorMsg('');
    setScanResult(null);
    setUploadFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (newMode === 'upload') {
      await stopCamera();
      startedRef.current = false;
    } else {
      startedRef.current = true;
      startCamera();
    }
    setScanMode(newMode);
  };

  // ── File upload scan ───────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFileName(file.name);
    setIsProcessingFile(true);
    setErrorMsg('');

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const instance    = new Html5Qrcode('bb-event-qr-reader');
      const decodedText = await instance.scanFile(file, false);
      instance.clear();
      setIsProcessingFile(false);
      handleDecodedQR(decodedText);
    } catch (err) {
      setIsProcessingFile(false);
      setUploadFileName('');
      setErrorMsg('No QR code could be read from this image. Make sure the image is clear and contains the full QR code, then try again.');
      setPhase('error');
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Decode & look up registration ─────────────────────────────────────────
  const handleDecodedQR = async (rawText) => {
    setPhase('validating');

    // Try to find the registration — token is the raw QR text
    try {
      const result = await recordAttendanceByQR(orgId, rawText.trim(), adminId, /* dryRun */ true);

      if (!result.success) {
        setErrorMsg(result.error || 'QR code not recognised.');
        setPhase('error');
        return;
      }

      setScanResult(result);
      setPhase('confirming');
    } catch (err) {
      console.error('QR lookup error:', err);
      setErrorMsg('Failed to look up QR code. Please try again.');
      setPhase('error');
    }
  };

  // ── Confirm & record attendance ────────────────────────────────────────────
  const handleConfirm = async () => {
    setPhase('recording');
    try {
      const result = await recordAttendanceByQR(orgId, scanResult.qr_token, adminId, /* dryRun */ false);
      if (!result.success) {
        setErrorMsg(result.error || 'Failed to record attendance.');
        setPhase('error');
        return;
      }
      setPhase('success');
    } catch (err) {
      console.error('Attendance record error:', err);
      setErrorMsg('Failed to record attendance. Please try again.');
      setPhase('error');
    }
  };

  // ── Retry ──────────────────────────────────────────────────────────────────
  const handleRetry = async () => {
    setPhase('scanning');
    setErrorMsg('');
    setScanResult(null);
    setUploadFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (scanMode === 'camera') {
      await stopCamera();
      startedRef.current = false;
      setTimeout(() => { startedRef.current = true; startCamera(); }, 50);
    }
  };

  const isIdle = phase === 'scanning';

  const registrantLabel = (type) => {
    if (type === 'volunteer') return 'Volunteer';
    return 'Member';
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={styles.headerIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
                <path d="M14 14h3v3M17 17h3M17 20v1M20 14v3"/>
              </svg>
            </div>
            <h3 style={styles.title}>Scan Member QR Code</h3>
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ── Mode tabs ── */}
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(scanMode === 'camera' ? styles.tabActive : {}) }}
            onClick={() => handleModeSwitch('camera')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ marginRight: 6, flexShrink: 0 }}>
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
            Use Camera
          </button>
          <button
            style={{ ...styles.tab, ...(scanMode === 'upload' ? styles.tabActive : {}) }}
            onClick={() => handleModeSwitch('upload')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ marginRight: 6, flexShrink: 0 }}>
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            Upload Image
          </button>
        </div>

        {/* ── Body ── */}
        <div style={styles.body}>

          {/* Camera feed — always in DOM; hidden when not active */}
          <div
            id="bb-event-qr-reader"
            style={{
              width: '100%',
              ...(scanMode === 'camera' && isIdle
                ? {}
                : { height: 0, overflow: 'hidden', visibility: 'hidden' })
            }}
          />

          {/* ── Upload mode idle UI ── */}
          {scanMode === 'upload' && isIdle && (
            <div style={styles.centreBlock}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />

              {isProcessingFile ? (
                <>
                  <div style={styles.spinner} />
                  <p style={styles.statusText}>Reading QR code from image…</p>
                  {uploadFileName && (
                    <p style={{ ...styles.statusText, fontSize: 12, color: '#aaa' }}>{uploadFileName}</p>
                  )}
                </>
              ) : (
                <>
                  <div style={styles.uploadZone} onClick={() => fileInputRef.current?.click()}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
                      stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/>
                      <path d="M14 14h3v3M17 17h3M17 20v1M20 14v3"/>
                    </svg>
                    <p style={styles.uploadTitle}>Choose an image to scan</p>
                    <p style={styles.uploadSub}>
                      Select a screenshot or photo of the member's QR code from your gallery or WhatsApp
                    </p>
                    <button
                      style={styles.chooseBtn}
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    >
                      Browse Gallery / Photos
                    </button>
                  </div>
                  <p style={styles.uploadHint}>Supported: PNG, JPG, WEBP, HEIC and most image formats</p>
                </>
              )}
            </div>
          )}

          {/* ── Validating ── */}
          {phase === 'validating' && (
            <div style={styles.centreBlock}>
              <div style={styles.spinner} />
              <p style={styles.statusText}>Looking up registration…</p>
            </div>
          )}

          {/* ── Recording ── */}
          {phase === 'recording' && (
            <div style={styles.centreBlock}>
              <div style={styles.spinner} />
              <p style={styles.statusText}>Recording attendance…</p>
            </div>
          )}

          {/* ── Error ── */}
          {phase === 'error' && (
            <div style={styles.centreBlock}>
              <div style={styles.errorIcon}>✕</div>
              <p style={styles.errorText}>{errorMsg}</p>
              {scanMode === 'camera' ? (
                <button style={styles.retryBtn} onClick={handleRetry}>Try Again</button>
              ) : (
                <button style={styles.retryBtn} onClick={() => { setPhase('scanning'); setErrorMsg(''); setUploadFileName(''); }}>
                  Choose Another Image
                </button>
              )}
            </div>
          )}

          {/* ── Confirming (registration found, awaiting admin confirmation) ── */}
          {phase === 'confirming' && scanResult && (
            <div style={styles.centreBlock}>
              <div style={styles.foundIcon}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                  stroke="#3A7D44" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <p style={styles.foundText}>Registration Found</p>

              <div style={styles.infoCard}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Name</span>
                  <span style={styles.infoValue}>{scanResult.member_name}</span>
                </div>
                {scanResult.member_email && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Email</span>
                    <span style={styles.infoValue}>{scanResult.member_email}</span>
                  </div>
                )}
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Type</span>
                  <span style={styles.infoValue}>{registrantLabel(scanResult.registrant_type)}</span>
                </div>
                <div style={styles.infoDivider} />
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Event</span>
                  <span style={styles.infoValue}>{scanResult.event_title || '—'}</span>
                </div>
                {scanResult.event_datetime && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Date & Time</span>
                    <span style={styles.infoValue}>{fmtDateTime(scanResult.event_datetime)}</span>
                  </div>
                )}
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Status</span>
                  <span style={{ ...styles.infoValue, color: '#f59e0b', fontWeight: 600 }}>
                    {scanResult.current_status}
                  </span>
                </div>
              </div>

              <div style={styles.actionRow}>
                <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
                <button style={styles.confirmBtn} onClick={handleConfirm}>
                  ✓ Record Attendance
                </button>
              </div>
            </div>
          )}

          {/* ── Success ── */}
          {phase === 'success' && scanResult && (
            <div style={styles.centreBlock}>
              <div style={styles.successIcon}>✓</div>
              <p style={styles.successText}>Attendance Recorded</p>

              <div style={styles.infoCard}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Name</span>
                  <span style={styles.infoValue}>{scanResult.member_name}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Type</span>
                  <span style={styles.infoValue}>{registrantLabel(scanResult.registrant_type)}</span>
                </div>
                <div style={styles.infoDivider} />
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Event</span>
                  <span style={styles.infoValue}>{scanResult.event_title || '—'}</span>
                </div>
                {scanResult.event_datetime && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Date & Time</span>
                    <span style={styles.infoValue}>{fmtDateTime(scanResult.event_datetime)}</span>
                  </div>
                )}
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Checked In</span>
                  <span style={{ ...styles.infoValue, color: '#3A7D44', fontWeight: 600 }}>✓ Now</span>
                </div>
              </div>

              <div style={styles.actionRow}>
                <button style={styles.scanNextBtn} onClick={handleRetry}>Scan Next</button>
                <button style={styles.confirmBtn} onClick={onClose}>Done</button>
              </div>
            </div>
          )}

        </div>

        {/* ── Hint footer ── */}
        {isIdle && scanMode === 'camera' && (
          <p style={styles.hint}>
            Point your camera at the member or volunteer's personal QR code.
          </p>
        )}

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    background: '#fff', borderRadius: 12,
    width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    display: 'flex', flexDirection: 'column'
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid #e2e8f0'
  },
  headerIcon: {
    width: 34, height: 34, borderRadius: 8,
    background: '#f0fdf4', color: '#3A7D44',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  title: { margin: 0, fontSize: 15, fontWeight: 600, color: '#1e293b' },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 22, color: '#94a3b8', lineHeight: 1, padding: '0 4px'
  },
  tabs: {
    display: 'flex', borderBottom: '1px solid #e2e8f0',
    padding: '0 20px', gap: 4
  },
  tab: {
    display: 'flex', alignItems: 'center',
    padding: '10px 14px', fontSize: 13, fontWeight: 500,
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    cursor: 'pointer', color: '#94a3b8', marginBottom: -1,
    transition: 'color 0.15s, border-color 0.15s'
  },
  tabActive: { color: '#3A7D44', borderBottomColor: '#3A7D44' },
  body: {
    padding: 20,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16
  },
  centreBlock: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 12, width: '100%', textAlign: 'center'
  },
  uploadZone: {
    width: '100%', padding: '28px 20px',
    border: '2px dashed #cbd5e1', borderRadius: 12,
    background: '#f8fafc', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10
  },
  uploadTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: '#1e293b' },
  uploadSub: { margin: 0, fontSize: 12, color: '#94a3b8', maxWidth: 280, lineHeight: 1.5 },
  chooseBtn: {
    marginTop: 4, padding: '9px 20px', borderRadius: 8,
    border: 'none', background: '#3A7D44',
    cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600
  },
  uploadHint: { margin: 0, fontSize: 11, color: '#cbd5e1' },
  spinner: {
    width: 40, height: 40,
    border: '3px solid #e2e8f0', borderTop: '3px solid #3A7D44',
    borderRadius: '50%', animation: 'bb-event-spin 0.8s linear infinite'
  },
  statusText: { color: '#64748b', fontSize: 14, margin: 0 },
  errorIcon: {
    width: 52, height: 52, borderRadius: '50%',
    background: '#fee2e2', color: '#dc2626',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 24, fontWeight: 700
  },
  errorText: { color: '#dc2626', fontSize: 14, margin: 0, maxWidth: 300 },
  foundIcon: {
    width: 52, height: 52, borderRadius: '50%',
    background: '#f0fdf4',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  foundText: { color: '#3A7D44', fontSize: 15, fontWeight: 600, margin: 0 },
  successIcon: {
    width: 52, height: 52, borderRadius: '50%',
    background: '#dcfce7', color: '#16a34a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 24, fontWeight: 700
  },
  successText: { color: '#16a34a', fontSize: 15, fontWeight: 600, margin: 0 },
  infoCard: {
    background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 10, padding: '14px 16px', width: '100%', textAlign: 'left'
  },
  infoRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: 8, marginBottom: 8
  },
  infoLabel: { fontSize: 12, color: '#94a3b8', flexShrink: 0, paddingTop: 1 },
  infoValue: { fontSize: 13, color: '#1e293b', fontWeight: 500, textAlign: 'right' },
  infoDivider: { borderTop: '1px solid #e2e8f0', margin: '8px 0' },
  actionRow: { display: 'flex', gap: 10, width: '100%', marginTop: 4 },
  cancelBtn: {
    flex: 1, padding: 10, borderRadius: 8,
    border: '1px solid #e2e8f0', background: '#fff',
    cursor: 'pointer', fontSize: 14, color: '#64748b'
  },
  confirmBtn: {
    flex: 2, padding: 10, borderRadius: 8,
    border: 'none', background: '#3A7D44',
    cursor: 'pointer', fontSize: 14, color: '#fff', fontWeight: 600
  },
  scanNextBtn: {
    flex: 1, padding: 10, borderRadius: 8,
    border: '1px solid #3A7D44', background: '#fff',
    cursor: 'pointer', fontSize: 14, color: '#3A7D44', fontWeight: 600
  },
  retryBtn: {
    padding: '10px 24px', borderRadius: 8,
    border: 'none', background: '#3A7D44',
    cursor: 'pointer', fontSize: 14, color: '#fff', fontWeight: 600
  },
  hint: {
    fontSize: 12, color: '#94a3b8', textAlign: 'center',
    padding: '0 20px 16px', margin: 0
  }
};

if (typeof document !== 'undefined' && !document.getElementById('bb-event-qr-keyframe')) {
  const s = document.createElement('style');
  s.id = 'bb-event-qr-keyframe';
  s.textContent = '@keyframes bb-event-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

export default EventQRScannerModal;
